import { useCallback, useEffect, useState } from "react";

import { colors } from "theme/palette";
import { format, parseJSON } from "utils/datetime";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import PageHeader from "components/PageHeader";
import Stack from "components/Stack";
import Text from "components/Text";

import FileTree from "entities/DDPackageZip/components/FileTree";
import ZipList from "entities/DDPackageZip/components/ZipList";
import {
  IZipList,
  startCreatingZipFile,
  useZipList,
} from "entities/DDPackageZip/sdk";
import { snapshotZIPFileSignedURLRetrieve } from "entities/Package/sdk";
import { useZipProcessing } from "entities/TableStorage/hooks";

export interface IZipDetails {
  dealId: number;
  packageId: number;
}

const ZipDetails = ({ dealId, packageId }: IZipDetails) => {
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

  const [latestZip, setLatestZip] = useState<undefined | IZipList>(undefined);

  const { data: zipList, mutate: updateZipList } = useZipList({
    dealId,
    packageId,
  });

  useEffect(() => {
    if (zipList) {
      setLatestZip(
        zipList.results.find((zip: IZipList) => zip.finished_at && !zip.failed)
      );
    }
  }, [zipList]);

  const handleUpdateClick = useCallback(() => {
    setIsUpdateButtonDisabled(true);

    startCreatingZipFile({ dealId, packageId }).then(() => updateZipList());
  }, [dealId, packageId, updateZipList]);

  const handleDownloadClick = useCallback(
    ({
      dealId,
      companyPackageId,
      zipId,
      zipName,
    }: {
      dealId: number;
      companyPackageId: number;
      zipId: number;
      zipName: string;
    }) => {
      snapshotZIPFileSignedURLRetrieve({
        dealId,
        companyPackageId,
        zipId,
      })
        .then((url) =>
          downloadFileWithFileName({
            fileUrl: url,
            fileName: zipName,
          })
        )
        .catch(handleInvalidRequest);
    },
    []
  );

  const eventHandler = useCallback(() => {
    updateZipList();
    setIsUpdateButtonDisabled(false);
  }, [updateZipList]);

  useZipProcessing({
    successEventHandler: eventHandler,
    failureEventHandler: eventHandler,
  });

  return (
    <Stack spacing={4} data-testid="checklist-zip-details-content">
      <Stack spacing={2} overflow="auto" flex={1}>
        <Stack spacing={2}>
          <PageHeader
            title="Update before downloading to include recent files!"
            subTitle={
              <>
                {latestZip && (
                  <>
                    <Text
                      variant="text3"
                      component="span"
                      sx={{ color: colors.gray80 }}
                    >
                      Last updated by{" "}
                    </Text>
                    <Text
                      variant="text3"
                      component="span"
                      sx={{ fontWeight: 500, color: colors.gray80 }}
                      data-testid="updated-by-user-name"
                    >
                      {latestZip.created_by.first_name},{" "}
                    </Text>
                    <Text
                      variant="text3"
                      component="span"
                      sx={{ fontWeight: 500, color: colors.gray80 }}
                      data-testid="updated-date"
                    >
                      {format(
                        parseJSON(latestZip.created_at),
                        "MMM dd, yyyy, hh:mm a"
                      )}
                    </Text>
                  </>
                )}
              </>
            }
            actions={
              <Stack spacing={2} direction="row">
                <Button
                  variant="outlined"
                  onClick={handleUpdateClick}
                  data-testid="create-update-zip-button"
                  disabled={isUpdateButtonDisabled}
                >
                  {latestZip ? "Update" : "Create"}
                </Button>
                {latestZip && (
                  <Button
                    onClick={() =>
                      handleDownloadClick({
                        zipId: latestZip.id,
                        dealId: dealId,
                        companyPackageId: packageId,
                        zipName: latestZip.name,
                      })
                    }
                    data-testid="latest-zip-download-button"
                  >
                    Download ZIP
                  </Button>
                )}
              </Stack>
            }
          />
          <Stack
            direction="row"
            spacing={2}
            alignItems="stretch"
            data-testid="zip-page-content-body"
          >
            <FileTree dealId={dealId} packageId={packageId} />
            <ZipList dealId={dealId} packageId={packageId} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ZipDetails;
